import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  MinusCircle as MinusCircleIcon,
  PauseCircle as PauseCircleIcon,
} from 'react-feather'
import clsx from 'clsx'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { reverse } from 'named-urls'
import { Alert } from '@material-ui/lab'

import {
  Container,
  ContentHeader,
  DriveTour,
  LoadingFeedback,
  MenuButton,
  Page,
  Permitted,
} from 'components'
import {
  ConsentDetailsForm,
  ConsentDetailsInfo,
  ConsentDetailsPublishSection,
  DeleteFormModal,
  InactiveFormModal,
} from './components'

import useFetch from 'hooks/useFetch'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'
import permissions from 'constants/permissions'

import useStyles from './styles'
import { routes } from 'Routes'

const ConsentDetails = ({ match }) => {
  const { consentFormId } = match.params

  const [loading, setLoading] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openInactiveModal, setOpenInactiveModal] = useState(false)
  const [openTour, setOpenTour] = useState(false)

  const { CONSENT_DETAIL_FUNCTION_TABS } = constants.consent
  const [tab, setTab] = useState(CONSENT_DETAIL_FUNCTION_TABS[0].value)

  const history = useHistory()
  const classes = useStyles()

  const {
    response: consentFormResponse,
    isLoading: consentFormIsLoading,
    refresh: consentFormRefresh,
  } = useFetch(
    service.dponet.consent.get,
    {
      consentFormId,
    },
    [consentFormId],
  )

  const {
    response: usersResponse,
    isLoading: usersIsLoading,
    refresh: usersRefresh,
  } = useFetch(service.dponet.users.get, {
    perPage: 10000,
    minimal: true,
    active: true,
    userCompanyStatus: constants.userCompanies.ACTIVE,
    // DPO terceiro é recusado pelo backend ao submeter, então não deve aparecer no autocomplete.
    excludeOutsourcedDpo: true,
  })

  const users = usersResponse?.data?.users || []

  const consentForm = consentFormResponse?.data?.consentForm

  const isActive =
    consentForm?.status === constants.consent.ACTIVE_CONSENT_FORM_STATUS_ID

  const isExpired =
    consentForm?.status === constants.consent.EXPIRED_CONSENT_FORM_STATUS_ID

  const handleOpenDeleteModal = () => setOpenDeleteModal(true)
  const handleOpenInactiveModal = () => setOpenInactiveModal(true)
  const handleChangeTab = (_, value) => setTab(value)
  const handleBack = () => history.push(reverse(routes.consent.all))

  const handleRedirectToEdit = (consentFormId) => {
    history.push(
      reverse(routes.consent.edit, {
        consentFormId,
      }),
    )
  }

  const handleOpenTour = () => {
    const where = 'Visualização de formulário'
    const title = 'Tour'

    const params = {
      action: 'tour-ver-detalhes-formularios-consentimento',
    }

    helpers.mixpanel.track(where, title, params)
    setOpenTour(true)
  }

  return (
    <Page title="Visualização de formulário">
      <LoadingFeedback
        open={loading || consentFormIsLoading || usersIsLoading}
      />
      <Container maxWidth={false}>
        <ContentHeader title="Ver detalhes">
          <Tooltip title="Iniciar o tour guiado">
            <Button
              id={constants.consent.DETAILS_DRIVER_STEP_0}
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
          <Permitted
            tag={[permissions.CONSENTS.SHOW, permissions.CHANGE_RESPONSIBLE]}
          >
            <MenuButton>
              <Permitted tag={permissions.CONSENTS.CHANGE_RESPONSIBLE}>
                <Button
                  color="secondary"
                  onClick={() => handleRedirectToEdit(consentFormId)}
                  fullWidth
                  size="small"
                >
                  Editar
                </Button>
              </Permitted>

              <Permitted
                tag={constants.permissions.CONSENTS.CHANGE_FORM_STATUS}
              >
                <Button
                  onClick={handleOpenInactiveModal}
                  color="secondary"
                  fullWidth
                  size="small"
                >
                  {isActive ? 'Inativar' : 'Ativar'} Formulário
                </Button>
              </Permitted>

              <Permitted tag={constants.permissions.CONSENTS.DESTROY}>
                <Button
                  onClick={handleOpenDeleteModal}
                  color="secondary"
                  fullWidth
                  size="small"
                >
                  Excluir formulário
                </Button>
              </Permitted>
            </MenuButton>
          </Permitted>
        </ContentHeader>

        {isExpired && (
          <Box marginBottom={1}>
            <Alert variant="filled" severity="info">
              <Typography variant="h5">
                Um formulário que teve sua data de validade expirada não pode
                ser reativado, caso deseje coletar consentimentos novamente,
                crie outro formulário de consentimentos.
              </Typography>
            </Alert>
          </Box>
        )}

        <Box>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={() => handleChangeTab}
          >
            {CONSENT_DETAIL_FUNCTION_TABS.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                onClick={(event) => handleChangeTab(event, tab.value)}
              />
            ))}
          </Tabs>
        </Box>
        <Paper variant="outlined" className={classes.consentDetailsContainer}>
          {tab === CONSENT_DETAIL_FUNCTION_TABS[0].value && (
            <ConsentDetailsInfo
              consentForm={consentForm}
              refresh={consentFormRefresh}
            />
          )}

          {tab === CONSENT_DETAIL_FUNCTION_TABS[1].value &&
            !consentFormIsLoading &&
            !usersIsLoading && (
              <ConsentDetailsForm
                setLoading={setLoading}
                users={users}
                usersRefresh={usersRefresh}
                consentForm={consentForm}
                consentFormRefresh={consentFormRefresh}
              />
            )}

          {tab === CONSENT_DETAIL_FUNCTION_TABS[2].value && (
            <ConsentDetailsPublishSection
              consentForm={consentForm}
              setLoading={setLoading}
            />
          )}

          <Box p={1} display="flex" justifyContent="flex-end" gridGap={10}>
            <Button variant="outlined" onClick={handleBack}>
              Voltar
            </Button>
            {tab === CONSENT_DETAIL_FUNCTION_TABS[1].value && (
              <Permitted tag={permissions.CONSENTS.CHANGE_RESPONSIBLE}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  form="assign-responsible-consent-details-form"
                  disabled={loading}
                >
                  Salvar
                </Button>
              </Permitted>
            )}
          </Box>
        </Paper>
      </Container>
      <DeleteFormModal
        consentForm={consentForm}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        loading={loading}
        setLoading={setLoading}
      />
      <InactiveFormModal
        consentForm={consentForm}
        open={openInactiveModal}
        setOpen={setOpenInactiveModal}
        loading={loading}
        setLoading={setLoading}
        refresh={consentFormRefresh}
        isActive={isActive}
      />
      <DriveTour
        stepsMatrix={constants.consent.DETAILS_DRIVER_STEPS_OBJECT}
        customStyle={classes.customPopover}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default ConsentDetails
